import { useFormikContext } from 'formik'
import type { PropsWithChildren } from 'react'
import { useEffect } from 'react'
import { Form as AntdForm } from '../../Form'
import type {
  FormProps as AntdFormProps,
  FormInstance as AntdFormInstance
} from '../../Form'
import { ErrorList } from './ErrorList'
import { FormItem } from './FormItem'
import { getAntdFormNamePathsFromFormikErrors } from './utils'

export type FormInstance<Values = any> = AntdFormInstance<Values>
export type FormProps<Values = any> = AntdFormProps<Values>

export const FormComponent = <Values = any,>({
  layout = 'vertical',
  size = 'large',
  scrollToFirstError = true,
  ...restProps
}: PropsWithChildren<FormProps<Values>>) => {
  const { ...formProps } = restProps

  const {
    handleReset,
    handleSubmit,
    initialValues,
    values,
    isValidating,
    isSubmitting,
    errors
  } = useFormikContext<Values>()
  const [form] = AntdForm.useForm<Values>()

  // TODO: use fields={fields} to keep values -> https://ant.design/components/form/#components-form-demo-global-state
  useEffect(() => form.setFieldsValue(values as any), [form, values])

  // scroll to first error on form submit
  useEffect(() => {
    if (scrollToFirstError && isSubmitting) {
      const fieldsWithError = getAntdFormNamePathsFromFormikErrors(errors)

      if (fieldsWithError.length) {
        // Note: antd form keep fields in the same order as they are on the screen, so we can relay on this order to find first inout with errors
        const firstAntdFormFieldWithError = form
          .getFieldsError()
          .find(antdFormField => {
            return fieldsWithError.includes(antdFormField.name?.[0])
          })

        if (firstAntdFormFieldWithError) {
          form.scrollToField(firstAntdFormFieldWithError.name, {
            block: 'center',
            scrollMode: 'always'
          })
        }
      }
    }
  }, [errors, form, isSubmitting, isValidating, scrollToFirstError])

  return (
    <AntdForm
      form={form}
      onReset={handleReset}
      onFinish={handleSubmit as FormProps<Values>['onFinish']}
      initialValues={initialValues as FormProps<Values>['initialValues']}
      noValidate
      action={formProps.action ?? '#'}
      layout={layout}
      size={size}
      {...formProps}
    />
  )
}

// should we do it with hoist-non-react-statics ?
export const Form = Object.assign(FormComponent, {
  ...AntdForm,
  Item: FormItem,
  ErrorList: ErrorList
})
